import { z } from "zod";
import type { Api } from "../../services/api";
import { hasLoggedInStore } from "../../domains/auth/stores/deferredDeepLinkStore";
import { localStore } from "../../lib/store";

/**
 * What first brought a visitor to Juisci: the `user_acquisitions` row of the
 * account they create (`POST /webapp/public/users`, `firstTouch`).
 *
 * Every page load is an arrival, read at boot (`index.tsx`) before anything
 * can rewrite the URL. The arrival is classified once:
 *
 * - `shared_link`: a member's link (`?shared=`, `/gpt/shared/:token`,
 *   `/v/:token`). Who shared it is `shareAttribution.ts`, not this record;
 * - `room_invite`: a room invite page (`/invite/:code`);
 * - `site`: a link of the marketing website, which names its control in a
 *   valid `site_cta`. Its `utm_*` and the other `site_*` values are kept too;
 * - `campaign`: any `utm_*` parameter;
 * - `referrer`: a page on another host (`document.referrer`);
 * - `direct`: none of these.
 *
 * Same rules as the share credit: first touch wins, kept 30 days, recorded
 * only on a device that has never been signed in. One exception: a `direct`
 * arrival gives way to the first later arrival that carries a source. An app
 * start is a `direct` arrival, and the installed app opens the page the
 * install wall handed to the store only after it has started once.
 *
 * So a website visit that follows an earlier arrival with a source (a shared
 * link, a campaign, another site) is not recorded. Whether the last arrival
 * with a source should win instead is an open product decision.
 *
 * The `site_*` parameters come from the website (apps/site), on every link
 * into the app. Each one is validated here, at the URL boundary: an invalid
 * value is dropped, not cut. The api only caps lengths, and records an invalid
 * value as null rather than failing the signup.
 *
 * `installedViaWall`: the arrival carries the marker the mobile-web install
 * wall adds to that page (`withInstallWallMarker`). The marker is removed from
 * the address bar at boot (`removeInstallWallMarker`), so a URL that the
 * reader copies later does not carry it.
 *
 * The installed app cannot read the browser record. So the URL that the wall
 * hands to the store carries the record's `utm_*` and `site_*` values
 * (`withFirstTouchQuery`), and the app records the same campaign or website
 * arrival when it opens that URL.
 *
 * Known blind spots: a `referrer` first touch does not travel through the
 * install (only its URL parameters do); a fingerprint, clipboard and referrer
 * miss loses the page and the marker; and a device that has hosted an account
 * never records a first touch.
 */

const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const INSTALL_WALL_PARAM = "install_wall";

type SignupFirstTouch =
  Api["post"]["/webapp/public/users"]["input"]["json"]["firstTouch"];
type FirstTouchKind = NonNullable<SignupFirstTouch>["kind"];

const UTM_PARAMS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
} as const;

// The api caps each value. A longer one is cut rather than failing the signup.
const MAX_VALUE_LENGTH = 256;
const MAX_PATH_LENGTH = 2048;

const storedValue = z.string().max(MAX_VALUE_LENGTH).nullable();

type SiteLanguage = NonNullable<NonNullable<SignupFirstTouch>["siteLang"]>;

// The rules of the website's values (apps/site/src/attribution.ts), by query
// name. The api caps the same lengths.
const SITE_PARAMS = {
  siteCta: {
    name: "site_cta",
    schema: z
      .string()
      .max(64)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  },
  siteEntryPath: {
    name: "site_entry",
    schema: z
      .string()
      .max(256)
      .regex(/^\/[A-Za-z0-9._~/-]*$/),
  },
  siteReferrerHost: {
    name: "site_ref",
    schema: z
      .string()
      .max(253)
      .regex(
        /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
      ),
  },
  siteLang: {
    name: "site_lang",
    schema: z.enum(["en", "fr"] as const satisfies readonly SiteLanguage[]),
  },
} as const;

const firstTouchSchema = z
  .object({
    kind: z.enum([
      "shared_link",
      "room_invite",
      "site",
      "campaign",
      "referrer",
      "direct",
    ] as const satisfies readonly FirstTouchKind[]),
    utmSource: storedValue,
    utmMedium: storedValue,
    utmCampaign: storedValue,
    utmContent: storedValue,
    utmTerm: storedValue,
    referrerHost: storedValue,
    landingPath: z.string().max(MAX_PATH_LENGTH).nullable(),
    capturedAt: z.number(),
    installedViaWall: z.boolean(),
    siteCta: SITE_PARAMS.siteCta.schema.nullable(),
    siteEntryPath: SITE_PARAMS.siteEntryPath.schema.nullable(),
    siteReferrerHost: SITE_PARAMS.siteReferrerHost.schema.nullable(),
    siteLang: SITE_PARAMS.siteLang.schema.nullable(),
  })
  .nullable();

type FirstTouch = z.infer<typeof firstTouchSchema>;

const firstTouchStore = localStore("first-touch", null, firstTouchSchema);

function currentFirstTouch(now: number): FirstTouch {
  const firstTouch = firstTouchStore.get();
  if (firstTouch === null) return null;
  if (now - firstTouch.capturedAt <= ATTRIBUTION_WINDOW_MS) return firstTouch;
  firstTouchStore.set(null);
  return null;
}

function queryValue(
  query: URLSearchParams,
  name: (typeof UTM_PARAMS)[keyof typeof UTM_PARAMS]
): string | null {
  const value = query.get(name);
  // PostgreSQL TEXT refuses a NUL byte (`%00`): the api would drop the value.
  if (value === null || value === "" || value.includes("\u0000")) return null;
  return value.slice(0, MAX_VALUE_LENGTH);
}

/** A `site_*` value, or null when it is absent or breaks the rules. */
function siteValue<T>(
  query: URLSearchParams,
  param: { name: string; schema: z.ZodType<T> }
): T | null {
  const value = query.get(param.name);
  if (value === null) return null;
  const parsed = param.schema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

// Not `URL.canParse`: the iOS 15 webview the app still supports lacks it, and
// this runs at boot, before anything can catch.
function parsedHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/** The host of the page that linked here, null for none or this app. */
function referrerHostOf(referrer: string, ownHost: string): string | null {
  if (referrer === "") return null;
  const host = parsedHost(referrer);
  if (host === null || host === "" || host === ownHost) return null;
  return host.slice(0, MAX_VALUE_LENGTH);
}

// The path of these pages is a share token, a capability: never stored.
function holdsShareToken(pathname: string): boolean {
  return pathname.startsWith("/gpt/shared/") || pathname.startsWith("/v/");
}

const INVITE_PATH_PREFIX = "/invite/";

/**
 * The landing path to store. A capability in the path never is: a share token
 * drops the path, and a room invite keeps `/invite` without its code, which
 * lets anyone join the room. The api applies the invite rule too, for a record
 * captured before this one.
 */
function storedLandingPath(pathname: string): string | null {
  if (holdsShareToken(pathname)) return null;
  if (pathname.startsWith(INVITE_PATH_PREFIX)) return "/invite";
  return pathname.slice(0, MAX_PATH_LENGTH);
}

// The `utm_source` of every short link (`apps/api/src/router/redirect`).
const SHORT_LINK_UTM_SOURCE = "juisci_short_url";

/** The invite code of a room invite page, null for any other page. */
function inviteCodeOf(pathname: string): string | null {
  if (!pathname.startsWith(INVITE_PATH_PREFIX)) return null;
  const rest = pathname.slice(INVITE_PATH_PREFIX.length);
  const slash = rest.indexOf("/");
  return slash === -1 ? rest : rest.slice(0, slash);
}

/**
 * A `utm_*` value to store. The invite links copy the invite code into
 * `utm_source` (the QR codes), and a short link writes its own path into
 * `utm_content` (the code, or a slug that redirects to the invite and so
 * grants the same access). On an invite page, a value equal to the code is
 * not stored, nor the `utm_content` of a short link.
 */
function utmValue(
  query: URLSearchParams,
  name: (typeof UTM_PARAMS)[keyof typeof UTM_PARAMS],
  inviteCode: string | null
): string | null {
  const value = queryValue(query, name);
  return value !== null && value === inviteCode ? null : value;
}

function arrivalKind(
  query: URLSearchParams,
  pathname: string,
  referrerHost: string | null,
  siteCta: string | null
): FirstTouchKind {
  if (query.has("shared") || holdsShareToken(pathname)) return "shared_link";
  if (pathname.startsWith(INVITE_PATH_PREFIX)) return "room_invite";
  if (siteCta !== null) return "site";
  if (
    Object.values(UTM_PARAMS).some((name) => queryValue(query, name) !== null)
  )
    return "campaign";
  if (referrerHost !== null) return "referrer";
  return "direct";
}

/** Boot: record this arrival when it is the first touch of the device. */
export function captureFirstTouch(arrival: {
  location: Pick<Location, "search" | "pathname" | "host">;
  referrer: string;
}): void {
  if (hasLoggedInStore.get()) return;
  const now = Date.now();
  const recorded = currentFirstTouch(now);
  const query = new URLSearchParams(arrival.location.search);
  const { pathname } = arrival.location;
  const referrerHost = referrerHostOf(arrival.referrer, arrival.location.host);
  const siteCta = siteValue(query, SITE_PARAMS.siteCta);
  const kind = arrivalKind(query, pathname, referrerHost, siteCta);
  const installedViaWall = query.get(INSTALL_WALL_PARAM) === "1";
  if (recorded !== null) {
    const recordedHasSource =
      recorded.kind !== "direct" || recorded.installedViaWall;
    const arrivalHasSource = kind !== "direct" || installedViaWall;
    if (recordedHasSource || !arrivalHasSource) return;
  }
  const inviteCode = inviteCodeOf(pathname);
  firstTouchStore.set({
    kind,
    utmSource: utmValue(query, UTM_PARAMS.utmSource, inviteCode),
    utmMedium: utmValue(query, UTM_PARAMS.utmMedium, inviteCode),
    utmCampaign: utmValue(query, UTM_PARAMS.utmCampaign, inviteCode),
    utmContent:
      inviteCode !== null &&
      queryValue(query, UTM_PARAMS.utmSource) === SHORT_LINK_UTM_SOURCE
        ? null
        : utmValue(query, UTM_PARAMS.utmContent, inviteCode),
    utmTerm: utmValue(query, UTM_PARAMS.utmTerm, inviteCode),
    referrerHost,
    landingPath: storedLandingPath(pathname),
    capturedAt: now,
    installedViaWall,
    siteCta,
    siteEntryPath: siteValue(query, SITE_PARAMS.siteEntryPath),
    siteReferrerHost: siteValue(query, SITE_PARAMS.siteReferrerHost),
    siteLang: siteValue(query, SITE_PARAMS.siteLang),
  });
}

/** Boot, after the capture: the marker must not travel with a copied URL. */
export function removeInstallWallMarker(): void {
  const url = new URL(globalThis.location.href);
  if (!url.searchParams.has(INSTALL_WALL_PARAM)) return;
  url.searchParams.delete(INSTALL_WALL_PARAM);
  globalThis.history.replaceState(globalThis.history.state, "", url.toString());
}

/** The `firstTouch` of the account-creation body (`POST /webapp/public/users`). */
export function signupFirstTouch(): SignupFirstTouch {
  const firstTouch = currentFirstTouch(Date.now());
  if (firstTouch === null) return null;
  return {
    kind: firstTouch.kind,
    utmSource: firstTouch.utmSource,
    utmMedium: firstTouch.utmMedium,
    utmCampaign: firstTouch.utmCampaign,
    utmContent: firstTouch.utmContent,
    utmTerm: firstTouch.utmTerm,
    referrerHost: firstTouch.referrerHost,
    landingPath: firstTouch.landingPath,
    touchedAt: new Date(firstTouch.capturedAt).toISOString(),
    installedViaWall: firstTouch.installedViaWall,
    siteCta: firstTouch.siteCta,
    siteEntryPath: firstTouch.siteEntryPath,
    siteReferrerHost: firstTouch.siteReferrerHost,
    siteLang: firstTouch.siteLang,
  };
}

/**
 * The URL the install wall hands to the store, with the marker on it: the
 * installed app records that it came through the wall when it opens it.
 */
export function withInstallWallMarker(href: string): string {
  const url = new URL(href);
  url.searchParams.set(INSTALL_WALL_PARAM, "1");
  return url.toString();
}

/**
 * The URL that the install wall hands to the store, with the browser's first
 * touch on it when that touch is a campaign or a website arrival. The page the
 * wall hands over is not always the arrival page: a login or signup door
 * hands over the page the reader came from (`getPostLoginRedirectPath`),
 * which may not carry the arrival query. The record's values replace the URL's own, so the
 * installed app records the touch that the browser recorded.
 */
export function withFirstTouchQuery(href: string): string {
  const firstTouch = currentFirstTouch(Date.now());
  if (firstTouch === null) return href;
  switch (firstTouch.kind) {
    case "site":
    case "campaign":
      break;
    case "shared_link":
    case "room_invite":
    case "referrer":
    case "direct":
      return href;
    default:
      throw new Error(firstTouch.kind satisfies never);
  }
  const url = new URL(href);
  const values = [
    [UTM_PARAMS.utmSource, firstTouch.utmSource],
    [UTM_PARAMS.utmMedium, firstTouch.utmMedium],
    [UTM_PARAMS.utmCampaign, firstTouch.utmCampaign],
    [UTM_PARAMS.utmContent, firstTouch.utmContent],
    [UTM_PARAMS.utmTerm, firstTouch.utmTerm],
    [SITE_PARAMS.siteCta.name, firstTouch.siteCta],
    [SITE_PARAMS.siteEntryPath.name, firstTouch.siteEntryPath],
    [SITE_PARAMS.siteReferrerHost.name, firstTouch.siteReferrerHost],
    [SITE_PARAMS.siteLang.name, firstTouch.siteLang],
  ] as const;
  for (const [name, value] of values) {
    if (value === null) url.searchParams.delete(name);
    else url.searchParams.set(name, value);
  }
  return url.toString();
}

/**
 * After the account is created: the record has done its only job, and a later
 * account on this device, after any sign-out (a forced one included), must not
 * inherit it.
 */
export function clearFirstTouch(): void {
  firstTouchStore.set(null);
}
